import React from 'react';
import { apiClient } from '../../../../../../../../api';
import { UploadAction } from './reducer';

interface FileWithConfig {
  readonly file: File;
  readonly audioId?: string;
}

export async function uploadFiles(args: {
  stationId: string;
  files: readonly FileWithConfig[];
  dispatch: React.Dispatch<UploadAction>;
}) {
  const { stationId, files, dispatch } = args;

  dispatch({
    type: 'ALL_STARTED',
  });

  for (let i = 0; i < files.length; i++) {
    const fileWithConfig = files[i];

    dispatch({
      type: 'UPLOAD_STARTED',
      payload: {
        index: i,
      },
    });

    try {
      const groups = await apiClient.stations.fetchStationGroupsForStation(stationId);

      const onProgress = (progress: number) =>
        dispatch({
          type: 'UPLOAD_PROGRESS',
          payload: {
            index: i,
            progress,
          },
        });

      const data = groups.length
        ? await uploadAudioToGroups({
            fileWithConfig,
            groupIds: groups.map((g) => g.id),
            onProgress,
          })
        : await uploadAudioToStation({
            fileWithConfig,
            stationId: stationId,
            onProgress,
          });

      dispatch({
        type: 'UPLOAD_COMPLETED',
        payload: {
          index: i,
          overridden: data?._overridden,
        },
      });
    } catch (error: any) {
      dispatch({
        type: 'UPLOAD_ERROR',
        payload: {
          index: i,
          error,
        },
      });
    }
  }

  dispatch({
    type: 'ALL_COMPLETED',
  });
}

function uploadAudioToStation({
  fileWithConfig,
  stationId,
  onProgress,
}: {
  fileWithConfig: {
    file: File;
    audioId?: string;
  };
  stationId: string;
  onProgress: (progress: number) => void;
}) {
  return apiClient.audit.uploadAudio({
    file: fileWithConfig.file,
    audioId: fileWithConfig.audioId?.trim(),
    stationId: stationId,
    onUploadProgress: (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total!);

      onProgress(percentCompleted);
    },
  });
}

async function uploadAudioToGroups({
  fileWithConfig,
  groupIds,
  onProgress,
}: {
  fileWithConfig: {
    file: File;
    audioId?: string;
  };
  groupIds: string[];
  onProgress: (progress: number) => void;
}) {
  let data;

  for (const groupId of groupIds) {
    const groupPercentCompleted = 1 / groupIds.length;
    const percentAtStart = groupIds.indexOf(groupId) * groupPercentCompleted;

    data = await apiClient.audit.uploadAudio({
      file: fileWithConfig.file,
      audioId: fileWithConfig.audioId?.trim(),
      stationsGroupId: groupId,
      onUploadProgress: (progressEvent) => {
        const filePercentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total!,
        );

        onProgress(percentAtStart + groupPercentCompleted * filePercentCompleted);
      },
    });
  }

  return data;
}
